/**
 * sidebarResize.js — Enterprise Sidebar Resize Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * EXPORTS (11):
 *   initSidebarResize, destroySidebarResize,
 *   collapseSidebar, expandSidebar, toggleSidebar,
 *   setSidebarWidth, resetSidebarWidth, lockSidebar, unlockSidebar,
 *   saveSidebarLayout, restoreSidebarLayout, getSidebarState
 *
 * [ENTERPRISE UPGRADE]
 *  - Collapse/expand with animation
 *  - Persistent layout (localStorage)
 *  - Lock/unlock resize
 *  - Programmatic width API
 *  - Keyboard resize (← → while handle focused)
 *  - Double-click handle to collapse/restore
 *  - Snap-to-grid (10px increments with Shift)
 *  - Diagnostics / state inspector
 *  - Cleanup / destroy
 */

import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════
// STATE
// ══════════════════════════════════════════════════════════════

const LS_KEY = 'mikhmel_sidebar_layout';
const SNAP_GRID = 10; // px snap with Shift
const ANIM_MS = 250;
const KB_STEP = 20; // px per arrow key press

/** @type {Map<string, SidebarMeta>} */
const _sidebars = new Map(); // 'left' | 'right' → meta

let _abortController = null;
let _initialized = false;

/**
 * [12.0.16] С какой стороны экрана сейчас стоит панель.
 *
 * До обмена панелей «левая» всегда была слева, и направление тяги можно
 * было брать прямо из имени. Теперь имя говорит о содержимом, а не о месте.
 *
 * @param {boolean} isLeft — панель по имени левая
 * @returns {boolean} стоит ли она сейчас у левого края
 */
function _наЛевойСтороне(isLeft) {
  return document.body.classList.contains('sb-swapped') ? !isLeft : isLeft;
}

/**
 * @typedef {Object} SidebarMeta
 * @property {HTMLElement} el
 * @property {HTMLElement} handle
 * @property {boolean}     isLeft
 * @property {boolean}     locked
 * @property {boolean}     collapsed
 * @property {number}      minW
 * @property {number}      maxW
 * @property {number}      defaultW
 * @property {number}      preCollapseW — width before collapse (for restore)
 */

// ══════════════════════════════════════════════════════════════
// 1. INIT
// ══════════════════════════════════════════════════════════════

export function initSidebarResize() {
  if (_initialized) return;
  _initialized = true;
  _abortController = new AbortController();
  const signal = _abortController.signal;

  const handles = document.querySelectorAll('.resize-handle');

  handles.forEach(handle => {
    const isLeft = handle.dataset.resize === 'left';
    const sidebar = handle.closest(isLeft ? '.sidebar-left' : '.sidebar-right');
    if (!sidebar) return;

    const style = getComputedStyle(sidebar);
    const key = isLeft ? 'left' : 'right';

    /** @type {SidebarMeta} */
    const meta = {
      el: sidebar,
      handle,
      isLeft,
      locked: false,
      collapsed: false,
      minW: parseInt(style.minWidth, 10) || 160,
      maxW: parseInt(style.maxWidth, 10) || 500,
      defaultW: sidebar.offsetWidth,
      preCollapseW: sidebar.offsetWidth,
    };
    _sidebars.set(key, meta);

    // ── Drag resize ──
    let startX = 0, startW = 0, rafId = null;

    const onMouseMove = (e) => {
      if (meta.locked || meta.collapsed) return;
      if (e.type === 'touchmove') e.preventDefault();

      const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      const dx = cx - startX;
      // [12.0.16] После обмена панелей местами (body.sb-swapped) «левая»
      // панель стоит справа: тянуть её мышью вправо должно СУЖАТЬ, а не
      // расширять. Сторону берём из положения на экране, а не из имени.
      let newW = _наЛевойСтороне(isLeft) ? startW + dx : startW - dx;

      // Shift = snap to grid
      if (e.shiftKey) newW = Math.round(newW / SNAP_GRID) * SNAP_GRID;

      newW = Math.max(meta.minW, Math.min(newW, meta.maxW));

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        sidebar.style.width = `${newW}px`;
      });
    };

    const onMouseUp = () => {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      handle.classList.remove('dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onMouseMove, { passive: false });
      document.removeEventListener('touchend', onMouseUp);
      window.dispatchEvent(new Event('resize'));
      // Auto-save after drag
      _autoSave();
    };

    const onMouseDown = (e) => {
      if (e.button === 2 || meta.locked || meta.collapsed) return;
      e.preventDefault();
      startX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      startW = sidebar.offsetWidth;

      const cs = getComputedStyle(sidebar);
      meta.minW = parseInt(cs.minWidth, 10) || 160;
      meta.maxW = parseInt(cs.maxWidth, 10) || 500;

      handle.classList.add('dragging');
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.addEventListener('touchmove', onMouseMove, { passive: false });
      document.addEventListener('touchend', onMouseUp);
    };

    handle.addEventListener('mousedown', onMouseDown, { signal });
    handle.addEventListener('touchstart', onMouseDown, { passive: false, signal });

    // ── Double-click handle → collapse / restore ──
    handle.addEventListener('dblclick', () => {
      if (meta.collapsed) expandSidebar(key);
      else collapseSidebar(key);
    }, { signal });

    // ── Keyboard resize (handle must be focusable) ──
    if (!handle.hasAttribute('tabindex')) handle.setAttribute('tabindex', '0');
    handle.addEventListener('keydown', (e) => {
      if (meta.locked || meta.collapsed) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const factor = _наЛевойСтороне(isLeft) ? dir : -dir;   // [12.0.16] см. выше
        const step = e.shiftKey ? SNAP_GRID : KB_STEP;
        const cur = sidebar.offsetWidth;
        const newW = Math.max(meta.minW, Math.min(cur + factor * step, meta.maxW));
        sidebar.style.width = `${newW}px`;
        window.dispatchEvent(new Event('resize'));
        _autoSave();
      }
    }, { signal });
  });

  // Restore saved layout
  restoreSidebarLayout();

  log.debug(`[SidebarResize] Initialized — ${_sidebars.size} sidebars`);
}

// ══════════════════════════════════════════════════════════════
// 2. COLLAPSE / EXPAND / TOGGLE
// ══════════════════════════════════════════════════════════════

/**
 * Collapse sidebar with animation
 * @param {'left'|'right'} which
 */
export function collapseSidebar(which = 'left') {
  const meta = _sidebars.get(which);
  if (!meta || meta.collapsed) return;
  meta.preCollapseW = meta.el.offsetWidth;
  meta.collapsed = true;
  meta.el.style.transition = `width ${ANIM_MS}ms ease`;
  meta.el.style.width = '0px';
  meta.el.style.overflow = 'hidden';
  meta.handle.style.pointerEvents = 'none';
  setTimeout(() => {
    meta.el.style.transition = '';
    window.dispatchEvent(new Event('resize'));
    _autoSave();
  }, ANIM_MS);
  log.debug(`[SidebarResize] Collapsed ${which}`);
}

/**
 * Expand sidebar to pre-collapse width
 * @param {'left'|'right'} which
 */
export function expandSidebar(which = 'left') {
  const meta = _sidebars.get(which);
  if (!meta || !meta.collapsed) return;
  meta.collapsed = false;
  meta.el.style.transition = `width ${ANIM_MS}ms ease`;
  meta.el.style.width = `${meta.preCollapseW || meta.defaultW}px`;
  meta.el.style.overflow = '';
  meta.handle.style.pointerEvents = '';
  setTimeout(() => {
    meta.el.style.transition = '';
    window.dispatchEvent(new Event('resize'));
    _autoSave();
  }, ANIM_MS);
  log.debug(`[SidebarResize] Expanded ${which}`);
}

/**
 * Toggle collapse state
 * @param {'left'|'right'} which
 */
export function toggleSidebar(which = 'left') {
  const meta = _sidebars.get(which);
  if (!meta) return;
  meta.collapsed ? expandSidebar(which) : collapseSidebar(which);
}

// ══════════════════════════════════════════════════════════════
// 3. WIDTH / LOCK / RESET
// ══════════════════════════════════════════════════════════════

/**
 * Set sidebar width programmatically
 * @param {'left'|'right'} which
 * @param {number} px
 */
export function setSidebarWidth(which, px) {
  const meta = _sidebars.get(which);
  if (!meta || meta.collapsed) return;
  const clamped = Math.max(meta.minW, Math.min(px, meta.maxW));
  meta.el.style.width = `${clamped}px`;
  window.dispatchEvent(new Event('resize'));
  _autoSave();
}

/**
 * Reset to default width
 * @param {'left'|'right'} which
 */
export function resetSidebarWidth(which = 'left') {
  const meta = _sidebars.get(which);
  if (!meta) return;
  if (meta.collapsed) expandSidebar(which);
  meta.el.style.width = `${meta.defaultW}px`;
  window.dispatchEvent(new Event('resize'));
  _autoSave();
  log.debug(`[SidebarResize] Reset ${which} to ${meta.defaultW}px`);
}

/**
 * Lock sidebar — prevents resize
 * @param {'left'|'right'} which
 */
export function lockSidebar(which = 'left') {
  const meta = _sidebars.get(which);
  if (!meta) return;
  meta.locked = true;
  meta.handle.classList.add('locked');
  meta.handle.style.cursor = 'not-allowed';
  log.debug(`[SidebarResize] Locked ${which}`);
}

/**
 * Unlock sidebar
 * @param {'left'|'right'} which
 */
export function unlockSidebar(which = 'left') {
  const meta = _sidebars.get(which);
  if (!meta) return;
  meta.locked = false;
  meta.handle.classList.remove('locked');
  meta.handle.style.cursor = '';
  log.debug(`[SidebarResize] Unlocked ${which}`);
}

// ══════════════════════════════════════════════════════════════
// 4. PERSISTENCE
// ══════════════════════════════════════════════════════════════

/** Save current sidebar widths/states to localStorage */
export function saveSidebarLayout() {
  try {
    const layout = {};
    for (const [key, meta] of _sidebars) {
      layout[key] = {
        width: meta.collapsed ? meta.preCollapseW : meta.el.offsetWidth,
        collapsed: meta.collapsed,
        locked: meta.locked,
      };
    }
    localStorage.setItem(LS_KEY, JSON.stringify(layout));
    log.debug('[SidebarResize] Layout saved');
  } catch (e) {
    log.warn('[SidebarResize] Save error:', e.message);
  }
}

/** Restore sidebar layout from localStorage */
export function restoreSidebarLayout() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    const layout = JSON.parse(raw);
    for (const [key, state] of Object.entries(layout)) {
      const meta = _sidebars.get(key);
      if (!meta) continue;
      if (state.width) meta.el.style.width = `${state.width}px`;
      if (state.collapsed) {
        meta.preCollapseW = state.width || meta.defaultW;
        collapseSidebar(key);
      }
      if (state.locked) lockSidebar(key);
    }
    window.dispatchEvent(new Event('resize'));
    log.debug('[SidebarResize] Layout restored');
  } catch (e) {
    log.warn('[SidebarResize] Restore error:', e.message);
  }
}

// ══════════════════════════════════════════════════════════════
// 5. DIAGNOSTICS
// ══════════════════════════════════════════════════════════════

/**
 * Get full sidebar state for diagnostics
 * @returns {Object}
 */
export function getSidebarState() {
  const state = {};
  for (const [key, meta] of _sidebars) {
    state[key] = {
      currentWidth: meta.el.offsetWidth,
      defaultWidth: meta.defaultW,
      minWidth: meta.minW,
      maxWidth: meta.maxW,
      collapsed: meta.collapsed,
      locked: meta.locked,
      preCollapseWidth: meta.preCollapseW,
    };
  }
  return state;
}

// ══════════════════════════════════════════════════════════════
// 6. CLEANUP
// ══════════════════════════════════════════════════════════════

export function destroySidebarResize() {
  if (_abortController) {
    _abortController.abort();
    _abortController = null;
  }
  _sidebars.clear();
  _initialized = false;
  log.debug('[SidebarResize] Destroyed');
}

// ══════════════════════════════════════════════════════════════
// INTERNAL
// ══════════════════════════════════════════════════════════════

let _saveDebounce = null;
function _autoSave() {
  clearTimeout(_saveDebounce);
  _saveDebounce = setTimeout(saveSidebarLayout, 500);
}
